(() => {
  const SELECTOR = '[data-shoppable-reels]';

  class ShoppableReels {
    constructor(root) {
      this.root = root;
      this.rail = root.querySelector('[data-sr-rail]');
      this.modal = root.querySelector('[data-sr-modal]');
      this.reels = root.querySelector('[data-sr-reels]');
      this.reelItems = [...root.querySelectorAll('[data-sr-reel]')];
      this.previews = [...root.querySelectorAll('[data-sr-preview]')];
      this.activeIndex = 0;
      this.modalMuted = root.dataset.popupMuted === 'true';
      this.mainMuted = root.dataset.mainMuted !== 'false';
      this.autoplay = root.dataset.autoplay === 'true';
      this.openCartDrawer = root.dataset.openCartDrawer === 'true';
      this.lastFocused = null;
      this.wheelLocked = false;
      this.touchStartY = 0;
      this.toastTimer = null;
      this.observers = [];
      this.isMobile = window.matchMedia('(max-width: 767px)').matches;

      if (!this.modal || !this.reels) return;
      this.bind();
      this.setupPreviewVideos();
      this.setupModalObserver();
    }

    bind() {
      this.root.addEventListener('click', (event) => {
        const open = event.target.closest('[data-sr-open]');
        if (open) {
          event.preventDefault();
          this.open(Number(open.dataset.srOpen || 0), open);
          return;
        }

        if (event.target.closest('[data-sr-close]')) {
          this.close();
          return;
        }

        /*
         * Close when clicking anywhere on the modal background,
         * while preserving clicks inside the reel/video/product UI.
         */
        if (
          !this.modal.hidden &&
          event.target.closest('[data-sr-modal]') &&
          !event.target.closest('.sr-reel__layout') &&
          !event.target.closest('.sr-modal__nav') &&
          !event.target.closest('.sr-modal__close')
        ) {
          this.close();
          return;
        }

        if (event.target.closest('[data-sr-rail-prev]')) {
          this.scrollRail(-1);
          return;
        }

        if (event.target.closest('[data-sr-rail-next]')) {
          this.scrollRail(1);
          return;
        }

        if (event.target.closest('[data-sr-modal-prev]')) {
          this.goTo(this.activeIndex - 1);
          return;
        }

        if (event.target.closest('[data-sr-modal-next]')) {
          this.goTo(this.activeIndex + 1);
          return;
        }

        const mainPlay = event.target.closest('[data-sr-main-play]');
        if (mainPlay) {
          event.stopPropagation();
          this.toggleMainPlay(mainPlay.closest('[data-sr-preview]'));
          return;
        }

        const mainMute = event.target.closest('[data-sr-main-mute]');
        if (mainMute) {
          event.stopPropagation();
          this.toggleMainMute(mainMute.closest('[data-sr-preview]'));
          return;
        }

        const popupPlay = event.target.closest('[data-sr-popup-play]');
        if (popupPlay) {
          this.togglePopupPlay(popupPlay.closest('[data-sr-reel]'));
          return;
        }

        const popupMute = event.target.closest('[data-sr-popup-mute]');
        if (popupMute) {
          this.togglePopupMute();
          return;
        }

      });

      if (!this.isMobile) {
        this.reels.addEventListener('wheel', (event) => {
          if (Math.abs(event.deltaY) < 12 || this.wheelLocked) return;
          event.preventDefault();
          this.wheelLocked = true;
          this.goTo(this.activeIndex + (event.deltaY > 0 ? 1 : -1));
          window.setTimeout(() => { this.wheelLocked = false; }, 500);
        }, { passive: false });
      }

      document.addEventListener('keydown', (event) => {
        if (this.modal.hidden) return;

        if (event.key === 'Escape') this.close();
        if (event.key === 'ArrowDown' || event.key === 'PageDown') {
          event.preventDefault();
          this.goTo(this.activeIndex + 1);
        }
        if (event.key === 'ArrowUp' || event.key === 'PageUp') {
          event.preventDefault();
          this.goTo(this.activeIndex - 1);
        }
        if (event.key === ' ') {
          const active = this.reelItems[this.activeIndex];
          const target = event.target;
          if (target && ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'].includes(target.tagName)) return;
          event.preventDefault();
          this.togglePopupPlay(active);
        }
      });
    }

    setupPreviewVideos() {
      this.previews.forEach((preview) => {
        const video = preview.querySelector('video');
        if (!video) return;

        video.preload = 'none';
        video.muted = this.mainMuted;
        video.defaultMuted = this.mainMuted;
        this.syncPreviewState(preview);

        video.addEventListener('play', () => this.syncPreviewState(preview));
        video.addEventListener('pause', () => this.syncPreviewState(preview));

        if (!this.autoplay) return;

        const threshold = this.isMobile ? 0.82 : 0.65;

        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (this.modal && !this.modal.hidden) {
              video.pause();
              return;
            }

            if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
              this.pauseOtherPreviews(video);
              video.preload = 'metadata';
              video.play().catch(() => {});
            } else {
              video.pause();
              if (this.isMobile) video.preload = 'none';
            }
          });
        }, { threshold: [0, threshold, 1] });

        observer.observe(preview);
        this.observers.push(observer);
      });
    }

    setupModalObserver() {
      const threshold = this.isMobile ? 0.72 : 0.6;

      const observer = new IntersectionObserver((entries) => {
        if (this.modal.hidden) return;

        let best = null;

        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
        });

        if (best && best.intersectionRatio >= threshold) {
          const index = Number(best.target.dataset.index || 0);
          if (index !== this.activeIndex || this.reelItems[index]?.querySelector('video')?.paused) {
            this.activate(index);
          }
        }
      }, {
        root: this.reels,
        threshold: this.isMobile ? [0.72, 0.9] : [0.6, 0.85]
      });

      this.reelItems.forEach((item) => observer.observe(item));
      this.observers.push(observer);
    }

    open(index, trigger) {
      if (!this.reelItems.length) return;

      this.lastFocused = trigger || document.activeElement;
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('sr-modal-open');

      this.activeIndex = this.clampIndex(index);
      this.pauseAllPreviews();
      this.preparePopupVideos(this.activeIndex);
      this.applyPopupMuteState();
      this.reels.scrollTop = this.reelItems[this.activeIndex].offsetTop;

      requestAnimationFrame(() => {
        this.activate(this.activeIndex, true);
        this.reels.focus({ preventScroll: true });
      });
    }

    close() {
      this.pauseAllPopupVideos();
      this.releasePopupVideos();
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('sr-modal-open');

      if (this.autoplay) {
        window.setTimeout(() => this.resumeMostVisiblePreview(), 80);
      }

      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus({ preventScroll: true });
      }
    }

    goTo(index) {
      if (!this.reelItems.length) return;

      const next = this.clampIndex(index);
      this.activeIndex = next;
      this.reelItems[next].scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.setTimeout(() => this.activate(next), 250);
    }

    clampIndex(index) {
      return Math.max(0, Math.min(this.reelItems.length - 1, index));
    }

    activate(index, forcePlay = false) {
      this.activeIndex = this.clampIndex(index);
      this.preparePopupVideos(this.activeIndex);

      this.reelItems.forEach((item, itemIndex) => {
        const video = item.querySelector('video');
        if (!video) return;

        if (itemIndex === this.activeIndex) {
          video.muted = this.modalMuted;
          video.defaultMuted = this.modalMuted;
          const playPromise = video.play();

          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {
              if (!video.muted) {
                video.muted = true;
                this.modalMuted = true;
                this.applyPopupMuteState();
                video.play().catch(() => {});
              }
            });
          }

          if (forcePlay) video.currentTime = Math.max(0, video.currentTime);
          this.bindProgress(item, video);
        } else {
          video.pause();
        }

        this.syncPopupState(item);
      });
    }

    bindProgress(item, video) {
      if (video.dataset.srProgressBound === 'true') return;
      video.dataset.srProgressBound = 'true';

      video.addEventListener('timeupdate', () => {
        const progress = item.querySelector('[data-sr-progress]');
        if (!progress) return;
        const percent = video.duration ? (video.currentTime / video.duration) * 100 : 0;
        progress.style.width = `${Math.max(0, Math.min(100, percent))}%`;
      });

      video.addEventListener('play', () => this.syncPopupState(item));
      video.addEventListener('pause', () => this.syncPopupState(item));
      video.addEventListener('volumechange', () => this.syncPopupState(item));
    }

    applyPopupMuteState() {
      this.reelItems.forEach((item) => {
        const video = item.querySelector('video');
        if (video) video.muted = this.modalMuted;
        this.syncPopupState(item);
      });
    }

    togglePopupMute() {
      this.modalMuted = !this.modalMuted;
      this.applyPopupMuteState();

      const activeVideo = this.reelItems[this.activeIndex]?.querySelector('video');
      if (activeVideo && !activeVideo.paused) {
        activeVideo.play().catch(() => {});
      }
    }

    togglePopupPlay(item) {
      const video = item?.querySelector('video');
      if (!video) return;

      if (video.paused) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
      this.syncPopupState(item);
    }

    syncPopupState(item) {
      const video = item?.querySelector('video');
      if (!video) return;

      item.classList.toggle('is-playing', !video.paused);
      item.classList.toggle('is-unmuted', !video.muted && video.volume > 0);
    }

    pauseAllPopupVideos() {
      this.reelItems.forEach((item) => {
        const video = item.querySelector('video');
        if (video) video.pause();
        this.syncPopupState(item);
      });
    }

    pauseAllPreviews() {
      this.previews.forEach((preview) => {
        const video = preview.querySelector('video');
        if (!video) return;
        video.pause();
        if (this.isMobile) video.preload = 'none';
        this.syncPreviewState(preview);
      });
    }

    resumeMostVisiblePreview() {
      if (!this.autoplay || !this.previews.length) return;

      let bestPreview = null;
      let bestRatio = 0;

      this.previews.forEach((preview) => {
        const rect = preview.getBoundingClientRect();
        const visible = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
        const ratio = rect.height > 0 ? visible / rect.height : 0;

        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestPreview = preview;
        }
      });

      const video = bestPreview?.querySelector('video');
      if (video && bestRatio >= (this.isMobile ? 0.65 : 0.5)) {
        this.pauseOtherPreviews(video);
        video.preload = 'metadata';
        video.play().catch(() => {});
      }
    }

    preparePopupVideos(index) {
      this.reelItems.forEach((item, itemIndex) => {
        const video = item.querySelector('video');
        if (!video) return;

        const distance = Math.abs(itemIndex - index);
        if (distance <= 1) {
          video.preload = distance === 0 ? 'auto' : 'metadata';
        } else {
          video.preload = 'none';
          video.pause();
        }
      });
    }

    releasePopupVideos() {
      this.reelItems.forEach((item) => {
        const video = item.querySelector('video');
        if (!video) return;
        video.pause();
        video.preload = 'none';
      });
    }

    toggleMainPlay(preview) {
      const video = preview?.querySelector('video');
      if (!video) return;

      if (video.paused) {
        this.pauseOtherPreviews(video);
        video.play().catch(() => {});
      } else {
        video.pause();
      }

      this.syncPreviewState(preview);
    }

    toggleMainMute(preview) {
      const video = preview?.querySelector('video');
      if (!video) return;

      video.muted = !video.muted;
      if (!video.paused) video.play().catch(() => {});
      this.syncPreviewState(preview);
    }

    syncPreviewState(preview) {
      const video = preview?.querySelector('video');
      if (!video) return;

      preview.classList.toggle('is-playing', !video.paused);
      preview.classList.toggle('is-unmuted', !video.muted && video.volume > 0);
    }

    pauseOtherPreviews(exceptVideo) {
      this.previews.forEach((preview) => {
        const video = preview.querySelector('video');
        if (video && video !== exceptVideo) {
          video.pause();
          this.syncPreviewState(preview);
        }
      });
    }

    scrollRail(direction) {
      if (!this.rail) return;
      const first = this.rail.querySelector('[data-sr-preview]');
      const gap = parseFloat(getComputedStyle(this.rail).gap) || 0;
      const amount = (first?.getBoundingClientRect().width || this.rail.clientWidth) + gap;
      this.rail.scrollBy({ left: direction * amount * (document.dir === 'rtl' ? -1 : 1), behavior: 'smooth' });
    }

    getCartSectionIds() {
      const ids = new Set();

      const selectors = [
        'cart-drawer',
        '#CartDrawer',
        '#cart-drawer',
        '[data-cart-drawer]',
        '[data-cart-drawer-content]',
        '.hdt-drawer-cart',
        '.hdt-cart-drawer',
        '.hdt-mini-cart',
        '[aria-controls="CartDrawer"]',
        '[aria-controls="cart-drawer"]',
        '[data-cart-drawer-open]',
        '.hdt-site-nav_icon.hdt-menu-cart',
        '[id*="cart-icon" i]',
        '[class*="cart-count" i]'
      ];

      document.querySelectorAll(selectors.join(',')).forEach((element) => {
        const section = element.closest('[id^="shopify-section-"]');
        if (!section?.id) return;

        const sectionId = section.id.replace(/^shopify-section-/, '');
        if (sectionId) ids.add(sectionId);
      });

      return [...ids].slice(0, 5);
    }

    async addToCart(button) {
      if (button.disabled || button.classList.contains('is-loading')) return;

      const variantId = Number(button.dataset.variantId);
      if (!variantId) return;

      const label = button.querySelector('[data-sr-add-label]');
      const originalText = label?.textContent || '';
      const isArabic = document.documentElement.lang.startsWith('ar');
      const root = window.Shopify?.routes?.root || '/';
      const sectionIds = this.getCartSectionIds();

      button.classList.add('is-loading');
      button.disabled = true;

      try {
        const payload = {
          items: [{ id: variantId, quantity: 1 }]
        };

        if (sectionIds.length) {
          payload.sections = sectionIds;
          payload.sections_url = `${window.location.pathname}${window.location.search}`;
        }

        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.description || data.message || 'Unable to add item');
        }

        if (data.sections) {
          this.renderCartSections(data.sections);
        } else {
          await this.refreshDetectedCartSections(sectionIds);
        }

        const cartResponse = await fetch(`${root}cart.js`, {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        const cart = cartResponse.ok ? await cartResponse.json() : null;

        if (label) {
          label.textContent = isArabic ? 'تمت الإضافة ✓' : 'Added ✓';
        }

        this.showToast(
          isArabic ? 'تمت إضافة المنتج إلى السلة' : 'Product added to cart'
        );

        const detail = {
          source: 'shoppable-reels',
          data,
          cart
        };

        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail
        }));

        document.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail
        }));

        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'shoppable-reels:add',
            ...detail
          }
        }));

        if (this.openCartDrawer) {
          await new Promise((resolve) => {
            requestAnimationFrame(() => requestAnimationFrame(resolve));
          });

          this.tryOpenCartDrawer();
        }

        window.setTimeout(() => {
          if (label) label.textContent = originalText;
        }, 1800);
      } catch (error) {
        this.showToast(
          error.message ||
          (isArabic ? 'تعذر إضافة المنتج' : 'Could not add product')
        );
      } finally {
        button.classList.remove('is-loading');
        button.disabled = false;
      }
    }

    renderCartSections(sections) {
      Object.entries(sections || {}).forEach(([sectionId, html]) => {
        if (!html) return;

        const current = document.getElementById(`shopify-section-${sectionId}`);
        if (!current) return;

        const template = document.createElement('template');
        template.innerHTML = html.trim();

        const replacement =
          template.content.querySelector(`#shopify-section-${CSS.escape(sectionId)}`) ||
          template.content.firstElementChild;

        if (!replacement) return;

        current.replaceWith(replacement);
      });
    }

    async refreshDetectedCartSections(sectionIds = this.getCartSectionIds()) {
      if (!sectionIds.length) return;

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('sections', sectionIds.join(','));

        const response = await fetch(url.toString(), {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) return;

        const sections = await response.json();
        this.renderCartSections(sections);
      } catch (_) {
        // Theme events below still provide a fallback refresh path.
      }
    }

    tryOpenCartDrawer() {
      const selectors = [
        '[aria-controls="CartDrawer"]',
        '[aria-controls="cart-drawer"]',
        '[data-cart-drawer-open]',
        'button[name="cart"]',
        '.hdt-site-nav_icon.hdt-menu-cart'
      ];

      for (const selector of selectors) {
        const trigger = document.querySelector(selector);

        if (trigger) {
          trigger.click();
          return;
        }
      }

      const drawer = [
        document.querySelector('cart-drawer'),
        document.querySelector('#CartDrawer'),
        document.querySelector('#cart-drawer'),
        document.querySelector('.hdt-drawer-cart'),
        document.querySelector('.hdt-cart-drawer')
      ].filter(Boolean)[0];

      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }

      if (drawer) {
        drawer.removeAttribute('hidden');
        drawer.setAttribute('open', '');
        drawer.classList.add('is-open', 'active', 'hdt-drawer-open');
        drawer.setAttribute('aria-hidden', 'false');
      }
    }

    showToast(message) {
      const toast = this.root.querySelector('[data-sr-toast]');
      if (!toast) return;

      toast.textContent = message;
      toast.classList.add('is-visible');
      window.clearTimeout(this.toastTimer);
      this.toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
    }

    destroy() {
      this.observers.forEach((observer) => observer.disconnect());
      this.pauseAllPopupVideos();
    }
  }

  const instances = new WeakMap();

  const init = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new ShoppableReels(root));
    });
  };

  document.addEventListener('DOMContentLoaded', () => init());

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector(SELECTOR);
    const instance = root && instances.get(root);
    if (instance) instance.destroy();
  });
})();
